import { HttpClient } from "@angular/common/http";
import { Component, EventEmitter, inject, Input, Output } from "@angular/core";
import { injectTranslation } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/assetmanagement/i18n/scope";
import { firstValueFrom } from "rxjs";
import { StandingdataDataService } from "@app/standingdata/shared/standingdata.service";
import { DateTimeDisplayPipe } from "@app/shared/pipes/date-time-display.pipe";
import { AssetManagementService, AssetMetadata } from "@app/assetmanagement/services/assetmanagement-data.service";
import { Pages } from "@constants/pages/Pages";
import { FilterType } from "@enums/FilterType";
import { ModuleType } from "@enums/ModuleType";
import { ComparisonType } from "@enums/workspace/ComparisonType";
import { DisplayItemModel } from "@models/generic/DisplayItemModel";
import { IdGuidNameCombination } from "@models/generic/IdGuidNameCombination";
import { CerrixTreeItem } from "@models/tree/CerrixTreeItem";
import { FilterConfig } from "@models/workspace/FilterConfig";
import { SearchKeyword } from "@models/workspace/SearchKeyword";
import { TabService } from "@services/tabs/TabService";
import { LinkableComponent } from "@app/shared/linkable/linkable.component";

export interface AssetLinkModel {
    guid: string;
    name: string;
    reference: number | string;
    assetName: string;
    // The numeric organization id, which is what the advanced organization filter matches on.
    organizationId: number | null;
    displayItems: DisplayItemModel[];
}

/**
 * The linked assets section of an item's detail page. The item stores the links itself, so this
 * component only shows them and reports the linked asset ids when they change.
 */
@Component({
    selector: "app-linked-assets",
    templateUrl: "./linked-assets.component.html",
    imports: [LinkableComponent],
})
export class LinkedAssetsComponent {
    private _assetManagementService = inject(AssetManagementService);
    private _http = inject(HttpClient);
    private _standingDataService = inject(StandingdataDataService);
    private _tabService = inject(TabService);
    private _pages = inject(Pages);
    private _translation = injectTranslation(scope);
    private readonly _dateTimePipe = new DateTimeDisplayPipe();

    @Input() linkedAssetIds: string[] = [];
    // The organization of the item, which the organization filter selects by default.
    @Input() organizationId: number;
    @Input() disabled = true;
    @Output() linkedAssetIdsChange = new EventEmitter<string[]>();

    isLoading = true;
    linked: AssetLinkModel[] = [];
    unlinked: AssetLinkModel[] = [];
    filterConfig: FilterConfig[] = [];
    activeFilters: SearchKeyword[] = [];
    moduleType = ModuleType.AssetManagement;

    async load(): Promise<void> {
        this.isLoading = true;

        await this._translation.ready();

        this.filterConfig = [
            {
                label: this._translation.t("linkedAssets.filterOrganizations"),
                column: "organizationId",
                type: FilterType.Organization,
                hideActionColumn: true,
                param: "",
            },
        ];
        this.activeFilters = [
            <SearchKeyword>{
                col: "organizationId",
                searchValue: this.organizationId + "",
                specific: true,
                compareType: ComparisonType["Is equal to"],
            },
        ];

        const linkedAssetIds = this.linkedAssetIds ?? [];
        const assets = await firstValueFrom(
            this._assetManagementService.getLinkableAssetsVbeta({ linkedAssetIds }),
        );

        // The endpoint returns ids only, so the owner and organization names are resolved here.
        // Every asset needs its organization: a linked asset that is unlinked moves into the
        // filtered list, and the organization filter would hide it without one.
        const [organizations, ownerNames] = await Promise.all([
            this.getOrganizationsByGuid(assets),
            this.getOwnerNamesByGuid(assets),
        ]);

        this.linked = assets
            .filter((x) => linkedAssetIds.includes(x.assetId))
            .map((x) => this.toLinkModel(x, organizations, ownerNames));
        this.unlinked = assets
            .filter((x) => !linkedAssetIds.includes(x.assetId))
            .map((x) => this.toLinkModel(x, organizations, ownerNames));
        this.isLoading = false;
    }

    linkedItemsChanged(linked: AssetLinkModel[]): void {
        this.linkedAssetIdsChange.emit(linked.map((x) => x.guid));
    }

    openItem(item: AssetLinkModel): void {
        // The asset page closes itself when the user may not view the asset.
        this._tabService.generateTab(
            this._pages.AssetManagementView,
            item.guid,
            this._translation.t("linkedAssets.assetTabTitle", {
                reference: item.reference,
                name: item.assetName,
            }),
        );
    }

    // First the organizations the user can see; only the ones still missing are requested by
    // selection, which keeps the query string short.
    private async getOrganizationsByGuid(assets: AssetMetadata[]): Promise<Map<string, CerrixTreeItem>> {
        const organizationsByGuid = new Map<string, CerrixTreeItem>();
        const organizationGuids = distinct(assets.map((x) => x.owningOrganizationId));
        if (!organizationGuids.length) {
            return organizationsByGuid;
        }

        const addOrganizations = (items: CerrixTreeItem[]) => {
            for (const organization of items ?? []) {
                organizationsByGuid.set(organization.Guid?.toLowerCase(), organization);
                addOrganizations(organization.Children);
            }
        };

        addOrganizations(
            await firstValueFrom(this._standingDataService.getOrganizations(ModuleType.AssetManagement)),
        );

        const missingGuids = organizationGuids.filter((guid) => !organizationsByGuid.has(guid.toLowerCase()));
        if (missingGuids.length) {
            addOrganizations(
                await firstValueFrom(
                    this._standingDataService.getOrganizationsIncludingSelected(ModuleType.AssetManagement, missingGuids),
                ),
            );
        }

        return organizationsByGuid;
    }

    // First all users; only the owners still missing, such as deleted users, are requested by
    // selection, which keeps the query string short.
    private async getOwnerNamesByGuid(assets: AssetMetadata[]): Promise<Map<string, string>> {
        const ownerNames = new Map<string, string>();
        const ownerGuids = distinct(assets.map((x) => x.ownerId));
        if (!ownerGuids.length) {
            return ownerNames;
        }

        const addUsers = (users: IdGuidNameCombination[]) =>
            users.forEach((user) => ownerNames.set(user.Guid?.toLowerCase(), user.Name));

        addUsers(await firstValueFrom(this._http.get<IdGuidNameCombination[]>(`/api/users/list`)));

        const missingGuids = ownerGuids.filter((guid) => !ownerNames.has(guid.toLowerCase()));
        if (missingGuids.length) {
            addUsers(
                await firstValueFrom(
                    this._http.get<IdGuidNameCombination[]>(`/api/users/selected?selected=${missingGuids.join(",")}`),
                ),
            );
        }

        return ownerNames;
    }

    private toLinkModel(
        asset: AssetMetadata,
        organizations: Map<string, CerrixTreeItem>,
        ownerNames: Map<string, string>,
    ): AssetLinkModel {
        const organization = organizations.get(asset.owningOrganizationId?.toLowerCase());

        return {
            guid: asset.assetId,
            name: `${asset.reference} - ${asset.name}`,
            reference: asset.reference,
            assetName: asset.name,
            organizationId: organization?.ID ?? null,
            // Listed in the order they are shown, then reversed, because app-linkable shows them last to first.
            displayItems: [
                {
                    Header: this._translation.t("linkedAssets.assetTypeHeader"),
                    Value: this._translation.t("linkedAssets.assetTypeApplication"),
                },
                {
                    Header: this._translation.t("linkedAssets.descriptionHeader"),
                    Value: asset.description ?? "",
                },
                {
                    Header: this._translation.t("linkedAssets.dateInUseHeader"),
                    Value: asset.dateInUse ? this._dateTimePipe.transform(asset.dateInUse) : "",
                },
                {
                    Header: this._translation.t("linkedAssets.dateOutOfUseHeader"),
                    Value: asset.dateOutOfUse ? this._dateTimePipe.transform(asset.dateOutOfUse) : "",
                },
                {
                    Header: this._translation.t("linkedAssets.ownerHeader"),
                    Value: ownerNames.get(asset.ownerId?.toLowerCase()) ?? "",
                },
                {
                    Header: this._translation.t("linkedAssets.owningOrganizationHeader"),
                    Value: organization?.Name ?? "",
                },
            ].reverse(),
        };
    }
}

function distinct(values: string[]): string[] {
    return [...new Set(values.filter((x) => !!x))];
}
